/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import monday from "monday-sdk-js";

export const WHATSAPP_ONBOARDING_URL =
  "https://notifyer.whatsable.app/onboarding/whatsapp-connection";

export const WHATSABLE_AUTH_REQUIRED_MESSAGE =
  "Authorization required. Connect your WhatsAble account to continue.";

export const WHATSAPP_CONNECT_MESSAGE =
  "Connect your WhatsApp to our system.";

/** Shown in viewer-blocked screen and docs; matches product naming in Monday. */
export const NOTIFYER_APP_DISPLAY_NAME = "WhatsAble Notifyer";

/** Full-screen copy when session API marks the user as view-only (Monday viewer). */
export function viewerBlockedMessage(): string {
  return `As a viewer, you are unable to use ${NOTIFYER_APP_DISPLAY_NAME}. Viewers cannot access the Monday API, so this app cannot run correctly for your account.`;
}

export function guestModeBannerMessage(): string {
  return `Signed in as a guest — you can chat, use templates, and start new conversations. Workspace settings (labels) are only available to members and admins; ask one if you need changes there.`;
}

function normalizeBool(value: unknown): boolean {
  return value === true || value === "true" || value === 1 || value === "1";
}

/** Backend aligns these with the Monday session token; the client uses this JSON only (no JWT parsing). */
function pickSessionRoleFlags(data: unknown): {
  is_view_only: boolean;
  is_guest: boolean;
  is_admin: boolean;
} {
  if (typeof data !== "object" || data === null) {
    return { is_view_only: false, is_guest: false, is_admin: false };
  }
  const d = data as Record<string, unknown>;
  return {
    is_view_only: normalizeBool(d.is_view_only),
    is_guest: normalizeBool(d.is_guest),
    is_admin: normalizeBool(d.is_admin),
  };
}

export type SessionInitRejectReason =
  | { authorizationRequired: true; apiMessage?: string }
  | { whatsappSetupRequired: true; apiMessage?: string }
  | { viewerBlocked: true }
  | string;

function isAuthFailurePayload(data: unknown): data is {
  success: false;
  message?: string;
} {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { success?: boolean }).success === false
  );
}

function openWhatsAppOnboardingInNewTabNonBlocking(): void {
  const url = WHATSAPP_ONBOARDING_URL;
  try {
    void monday()
      .execute("openLinkInTab", { url })
      .catch(() => {
        window.open(url, "_blank", "noopener,noreferrer");
      });
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

interface AuthState {
  loading?: boolean;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  error: string | boolean | undefined;
  /** Session OK but Notifyer OAuth needed — show in-app messaging, not perpetual loading */
  authorizationRequired: boolean;
  /** Session OK but WhatsApp not linked (check/whatsapp/number returned success: false) */
  whatsappSetupRequired: boolean;
  /** View-only Monday user — block app (set when session rejects with viewerBlocked). */
  viewerBlocked: boolean;
  is_guest: boolean;
  is_admin: boolean;
  system_user_id: string | null;
  labels: Label[];
  token : string
}
export interface Label {
  id: number;
  label: string;
  user_id: string;
  keywords: string[];
  created_at: number;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isAuthenticating: false,
  loading: false,
  error: false,
  authorizationRequired: false,
  whatsappSetupRequired: false,
  viewerBlocked: false,
  is_guest: false,
  is_admin: false,
  system_user_id: null,
  labels: [],
  token : ""
};

export const fetchLabels = createAsyncThunk(
  "auth/fetchLabels",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      const response = await axios.get(
        "https://api.insightssystem.com/api:uYdlVwac/web/label_management",
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

/** Validates Monday session token, then checks WhatsApp is connected (same sessionToken). */
export const initializeMondaySession = createAsyncThunk<
  {
    token: string;
    systemUser?: string;
    is_guest: boolean;
    is_admin: boolean;
  },
  { sessionToken: string },
  { rejectValue: SessionInitRejectReason }
>(
  "auth/initializeMondaySession",
  async ({ sessionToken }, { rejectWithValue }) => {
    try {
      const sessionResponse = await axios.post(
        "https://api.insightssystem.com/api:B4vX-Ohe/monday/chat/session",
        { sessionToken },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionToken,
          },
        }
      );

      const sessionData = sessionResponse.data;

      if (isAuthFailurePayload(sessionData)) {
        return rejectWithValue({
          authorizationRequired: true,
          apiMessage:
            typeof sessionData.message === "string"
              ? sessionData.message
              : undefined,
        });
      }

      const roleFlags = pickSessionRoleFlags(sessionData);

      if (roleFlags.is_view_only) {
        return rejectWithValue({ viewerBlocked: true });
      }

      const checkResponse = await axios.post(
        "https://api.insightssystem.com/api:B4vX-Ohe/monday/chat/check/whatsapp/number",
        { sessionToken },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionToken,
          },
        }
      );

      const checkData = checkResponse.data;

      if (isAuthFailurePayload(checkData)) {
        openWhatsAppOnboardingInNewTabNonBlocking();
        return rejectWithValue({
          whatsappSetupRequired: true,
          apiMessage:
            typeof checkData.message === "string"
              ? checkData.message
              : undefined,
        });
      }

      return {
        token: sessionData.token,
        systemUser: sessionData.systemUserId,
        is_guest: roleFlags.is_guest,
        is_admin: roleFlags.is_admin,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data;
        const reqUrl = error.config?.url ?? "";
        if (isAuthFailurePayload(responseData)) {
          const msg =
            typeof responseData.message === "string"
              ? responseData.message
              : undefined;
          if (reqUrl.includes("check/whatsapp/number")) {
            openWhatsAppOnboardingInNewTabNonBlocking();
            return rejectWithValue({
              whatsappSetupRequired: true,
              apiMessage: msg,
            });
          }
          return rejectWithValue({
            authorizationRequired: true,
            apiMessage: msg,
          });
        }
      }
      return rejectWithValue("Session initialization failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login(state, action: PayloadAction<{ system_user_id: string }>) {
      state.isAuthenticated = true;
      state.system_user_id = action.payload.system_user_id;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.system_user_id = null;
      state.authorizationRequired = false;
      state.whatsappSetupRequired = false;
      state.viewerBlocked = false;
      state.is_guest = false;
      state.is_admin = false;
      state.token = "";
    },
    addLabel(state, action: PayloadAction<Label>) {
      state.labels.push(action.payload);
    },
    removeLabel(state, action: PayloadAction<number>) {
      state.labels = state.labels.filter(
        (label) => label.id !== action.payload
      );
    },
    addKeywordToLabel(
      state,
      action: PayloadAction<{ labelId: number; newKeyword: string }>
    ) {
      const { labelId, newKeyword } = action.payload;
      const label = state.labels.find((label) => label.id === labelId);
      if (label) {
        label.keywords.push(newKeyword);
      }
    },
    removeKeywordFromLabel(
      state,
      action: PayloadAction<{ labelId: number; removedKeyword: string }>
    ) {
      const { labelId, removedKeyword } = action.payload;
      const label = state.labels.find((label) => label.id === labelId);

      if (label) {
        label.keywords = label.keywords.filter(
          (keyword) => keyword !== removedKeyword
        );
      }
    },
    setIsAuthenticating: (state, action) => {
      state.isAuthenticating = action.payload;
    },
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabels.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchLabels.fulfilled,
        (state, action: PayloadAction<Label[]>) => {
          state.loading = false;
          state.labels = action.payload;
        }
      )
      .addCase(fetchLabels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(initializeMondaySession.pending, (state) => {
        state.isAuthenticating = true;
        state.error = false;
        state.authorizationRequired = false;
        state.whatsappSetupRequired = false;
        state.viewerBlocked = false;
      })
      .addCase(initializeMondaySession.fulfilled, (state, action) => {
        state.isAuthenticating = false;
        state.authorizationRequired = false;
        state.whatsappSetupRequired = false;
        state.viewerBlocked = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.system_user_id = action.payload.systemUser || null;
        state.is_guest = action.payload.is_guest;
        state.is_admin = action.payload.is_admin;
      })
      .addCase(initializeMondaySession.rejected, (state, action) => {
        state.isAuthenticating = false;
        state.isAuthenticated = false;
        state.token = "";
        state.system_user_id = null;
        state.viewerBlocked = false;
        state.is_guest = false;
        state.is_admin = false;
        const payload = action.payload as SessionInitRejectReason | undefined;
        if (
          typeof payload === "object" &&
          payload !== null &&
          "authorizationRequired" in payload &&
          payload.authorizationRequired
        ) {
          state.authorizationRequired = true;
          state.whatsappSetupRequired = false;
          state.viewerBlocked = false;
          state.error = false;
        } else if (
          typeof payload === "object" &&
          payload !== null &&
          "whatsappSetupRequired" in payload &&
          payload.whatsappSetupRequired
        ) {
          state.whatsappSetupRequired = true;
          state.authorizationRequired = false;
          state.viewerBlocked = false;
          state.error = false;
        } else if (
          typeof payload === "object" &&
          payload !== null &&
          "viewerBlocked" in payload &&
          payload.viewerBlocked
        ) {
          state.viewerBlocked = true;
          state.authorizationRequired = false;
          state.whatsappSetupRequired = false;
          state.error = false;
        } else {
          state.authorizationRequired = false;
          state.whatsappSetupRequired = false;
          state.viewerBlocked = false;
          state.error = (payload as string) ?? "Session initialization failed";
        }
      });
  },
});

export const {
  login,
  logout,
  addLabel,
  removeLabel,
  addKeywordToLabel,
  removeKeywordFromLabel,
  setIsAuthenticating,
  setIsAuthenticated
} = authSlice.actions;
export const authReducer = authSlice.reducer;
